import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAmostragem } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';

interface AmostragemModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAAmostragem) => void;
	dadosAmostragem?: CadernoLPMAAmostragem | null;
}

const defaultValues: CadernoLPMAAmostragem = {
	dataInicioAmostragem: null,
	dataTerminoAmostragem: null,
	responsavelAmostragem: '',
	tipoMaterialAmostradoFolhas: false,
	tipoMaterialAmostradoRaizes: false,
	tipoMaterialAmostradoGraos: false,
	tipoMaterialAmostradoOutros: false,
	outrosTipoMaterialAmostrado: '',
	numeroAMI: '',
	dataAprovacaoAMI: null,
	quantidadeMaterialAmostrado: '',
	observacoesAmostragem: '',
};

export default function AmostragemModal({ show, onHide, onSubmit, dadosAmostragem }: AmostragemModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm<CadernoLPMAAmostragem>({
		mode: 'all',
		defaultValues,
	});

	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		if (show) {
			const carregarAssinatura = async () => {
				try {
					const token = localStorage.getItem('token');
					if (token) {
						const decoded: any = JSON.parse(atob(token.split('.')[1]));
						const username = decoded.unique_name || '';
						
						const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
						if (data && data.assinaturaBase64) {
							setAssinaturaUsuario(data.assinaturaBase64);
						}
					}
				} catch (error) {
					// Usuário não tem assinatura cadastrada
				}
			};
			
			carregarAssinatura();
			
			// Carregar assinatura salva se existir
			if (dadosAmostragem?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(dadosAmostragem.assinaturaEnvolvidosBase64);
			} else {
				// Limpar se não houver assinatura salva
				setAssinaturaEnvolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosAmostragem?.nomesEnvolvidosAdicionaisJson));
		} else {
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, dadosAmostragem?.assinaturaEnvolvidosBase64, dadosAmostragem?.nomesEnvolvidosAdicionaisJson]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		} else if (dadosAmostragem) {
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			reset({
				...dadosAmostragem,
				dataInicioAmostragem: parseDate(dadosAmostragem.dataInicioAmostragem),
				dataTerminoAmostragem: parseDate(dadosAmostragem.dataTerminoAmostragem),
				dataAprovacaoAMI: parseDate(dadosAmostragem.dataAprovacaoAMI),
			});
		}
	}, [show, dadosAmostragem, reset]);

	const handleSubmitInterno = (dados: CadernoLPMAAmostragem) => {
		const dadosComAssinatura = {
			...dados,
			assinaturaEnvolvidosBase64: assinaturaEnvolvidos || null,
			nomesEnvolvidosAdicionaisJson: stringifyNomesEnvolvidos(nomesAdicionais),
		};
		onSubmit(dadosComAssinatura);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.AMOSTRAGENS_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de acesso dos envolvidos e das datas inicial/final de realização da atividade</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>


				<Row className="row-custom">
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							<Controller
								control={control}
								name="dataInicioAmostragem"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataInicioAmostragem && <span className="error-field">{errors.dataInicioAmostragem.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							<Controller
								control={control}
								name="dataTerminoAmostragem"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataTerminoAmostragem && <span className="error-field">{errors.dataTerminoAmostragem.message}</span>}
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Amostragem</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de material GM amostrado</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoFolhas" label="Folhas" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoRaizes" label="Raízes" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoGraos" label="Grãos / sementes" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAmostradoOutros" label="Outros (especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosTipoMaterialAmostrado"
							placeholder="Descreva o material quando a opção 'Outros' for selecionada"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Número da AMI</Form.Label>
							<Form.Control
								ref={register}
								name="numeroAMI"
								placeholder="Informe o número da AMI"
								maxLength={100}
							/>
							{errors.numeroAMI && <span className="error-field">{errors.numeroAMI.message}</span>}
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Data de aprovação</Form.Label>
							<Controller
								control={control}
								name="dataAprovacaoAMI"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataAprovacaoAMI && <span className="error-field">{errors.dataAprovacaoAMI.message}</span>}
						</Form.Group>
					</Col>
					<Col md={4}>
						<Form.Group>
							<Form.Label>Quantidade / material</Form.Label>
							<Form.Control
								ref={register}
								name="quantidadeMaterialAmostrado"
								placeholder="Ex.: 500 g (folhas)"
								maxLength={150}
							/>
							{errors.quantidadeMaterialAmostrado && <span className="error-field">{errors.quantidadeMaterialAmostrado.message}</span>}
						</Form.Group>
					</Col>
				</Row>

				<p className="mt-2">
					<small>
						* Anexar uma cópia da autorização de movimentação interna (AMI) emitida pela CIBio na pasta de campo — ABA AMIs.
						No caso de Terceiros, anexar também a cópia da Carta de Transferência entre CIBios.
					</small>
				</p>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesAmostragem"
							placeholder="Registre informações complementares sobre a atividade de amostragem"
							maxLength={500}
						/>
						{errors.observacoesAmostragem && <span className="error-field">{errors.observacoesAmostragem.message}</span>}
					</Form.Group>
				</Form.Row>

				<p className="mt-2">
					<small>
						Para registrar outra atividade, insira este formulário e abra novamente a amostragem pelo botão correspondente.
					</small>
				</p>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Inserir" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

